/**
 * SalesByItemSection — Inline summary for PortcoDetail page.
 * Self-contained: manages its own data fetching via useSalesByItemData.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { SalesPeriod } from "../utils/PowerBIApiCall";
import { useSalesByItemData } from "../hooks/useSalesByItemData";
import { MetricCard } from "./MetricCard";
import { PeriodSelector } from "./PeriodSelector";
import { SalesByItemChart } from "./SalesByItemChart";
import { formatCurrencyCompact as formatCurrency } from "../utils/format";

interface SalesByItemSectionProps {
  portcoId: string;
  enabled?: boolean;
  onAuthError?: (error: unknown) => void;
}

export function SalesByItemSection({
  portcoId,
  enabled = true,
  onAuthError,
}: SalesByItemSectionProps) {
  const [period, setPeriod] = useState<SalesPeriod>("ytd");
  const { items, summary, loading, error } = useSalesByItemData(
    portcoId,
    period,
    enabled,
    onAuthError,
  );

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
          <div className="h-7 w-36 bg-gray-100 rounded animate-pulse" />
        </div>
        <div className="p-6">
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="bg-gray-50 border border-gray-100 rounded-xl p-5"
              >
                <div className="h-3 w-20 bg-gray-100 rounded animate-pulse mx-auto mb-3" />
                <div className="h-7 w-28 bg-gray-200 rounded animate-pulse mx-auto" />
              </div>
            ))}
          </div>
          <div className="h-64 bg-gray-50 rounded-lg animate-pulse" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white border border-red-100 rounded-xl shadow-sm overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <svg
              className="w-5 h-5 text-orange-500 flex-shrink-0"
              width={20}
              height={20}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
              />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900">
              Sales by Item
            </h2>
          </div>
          <PeriodSelector value={period} onChange={setPeriod} />
        </div>
        <div className="px-6 py-8 text-center">
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (items.length === 0) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <svg
              className="w-5 h-5 text-orange-500 flex-shrink-0"
              width={20}
              height={20}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
              />
            </svg>
            <h2 className="text-lg font-semibold text-gray-900">
              Sales by Item
            </h2>
          </div>
          <PeriodSelector value={period} onChange={setPeriod} />
        </div>
        <div className="px-6 py-8 text-center">
          <p className="text-sm text-gray-400">
            No sales data available for this period.
          </p>
        </div>
      </div>
    );
  }

  const periodLabels: Record<SalesPeriod, string> = {
    mtd: "Month to Date",
    qtd: "Quarter to Date",
    ytd: "Year to Date",
  };

  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden mb-6">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <svg
            className="w-5 h-5 text-orange-500 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
            />
          </svg>
          <h2 className="text-lg font-semibold text-gray-900">Sales by Item</h2>
          <span className="text-xs text-gray-400">{periodLabels[period]}</span>
        </div>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <MetricCard
            label="Total Revenue"
            value={formatCurrency(summary.totalRevenue)}
            highlighted
          />
          <MetricCard
            label="Gross Profit"
            value={formatCurrency(summary.totalGrossProfit)}
          />
          <MetricCard
            label="Gross Margin"
            value={`${summary.totalGrossMargin.toFixed(1)}%`}
          />
        </div>

        {/* Chart */}
        <SalesByItemChart items={items} maxItems={10} height={320} />

        {/* View All Link */}
        <div className="mt-4 pt-4 border-t border-gray-100 text-center">
          <Link
            to={`/portco/${portcoId}/sales-by-item`}
            className="inline-flex items-center gap-1.5 text-sm text-wmp-500 hover:text-wmp-700 font-medium transition-colors no-underline"
          >
            View All Items
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
}
